import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const COLORS = ["#032c58", "#b30202"]; // NAUY (bleu), NAUN (rouge)

export interface StatItem {
  NAUYMint: number;
  NAUNMint: number;
}

export interface PieChartSectionProps {
  data: StatItem[];
}

const PieChartSection: React.FC<PieChartSectionProps> = ({ data }) => {
  if (!data?.length) return null;

  const fallbackValue = 0;

  const realNAUY = data.reduce((sum, item) => sum + (item.NAUYMint ?? 0), 0);
  const realNAUN = data.reduce((sum, item) => sum + (item.NAUNMint ?? 0), 0);

  const totalNAUY = fallbackValue + realNAUY;
  const totalNAUN = fallbackValue + realNAUN;
  const totalAll = totalNAUY + totalNAUN || 1;

  const pieData = [
    { name: "NAUY Minted", value: totalNAUY },
    { name: "NAUN Minted", value: totalNAUN },
  ];

  const nauyPct = ((totalNAUY / totalAll) * 100).toFixed(1);
  const naunPct = ((totalNAUN / totalAll) * 100).toFixed(1);

  return (
    <motion.div
      role="region"
      aria-labelledby="pie-chart-title"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mt-12"
    >
      <h2
        id="pie-chart-title"
        className="text-4xl font-bold mb-10 text-center text-red-800"
      >
        Mint Distribution
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 bg-white/90 p-8 rounded-2xl shadow-xl max-w-6xl mx-auto">
        {/* Left Text */}
        <div className="flex flex-col justify-center">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Explanation
          </h3>
          <p className="text-sm text-gray-600">
            This pie chart illustrates the proportion of <strong>NAUY</strong>{" "}
            and <strong>NAUN</strong> tokens minted during the selected time
            frame. A higher mint ratio may indicate stronger user engagement or
            preference in community sentiment.
          </p>
        </div>

        {/* Chart */}
        <div className="flex flex-col items-center justify-center">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                outerRadius={90}
                labelLine={false}
              >
                {pieData.map((_, idx) => (
                  <Cell key={idx} fill={COLORS[idx]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  value.toLocaleString(),
                  name,
                ]}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-blue-800 rounded-full"></span>
              <span className="font-medium text-gray-800">
                NAUY: {nauyPct}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-red-700 rounded-full"></span>
              <span className="font-medium text-gray-800">
                NAUN: {naunPct}%
              </span>
            </div>
          </div>
        </div>

        {/* Right Totals */}
        <div className="flex flex-col justify-center items-center text-center">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Totals (Selected Range)
          </h3>
          <div>
            <p className="text-2xl font-bold text-blue-800">
              {totalNAUY.toLocaleString()}
            </p>
            <p className="text-xs uppercase text-gray-500">NAUY Minted</p>
          </div>
          <div className="mt-4">
            <p className="text-2xl font-bold text-red-700">
              {totalNAUN.toLocaleString()}
            </p>
            <p className="text-xs uppercase text-gray-500">NAUN Minted</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PieChartSection;
